import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_ALL_ASSETS } from '../graph-queries/tar/asset';
import { GET_ALL_EVENTS } from '../graph-queries/tar/event';
import { GET_CONFIG } from '../graph-queries/common/config';

export const nameToInitials = (name: string) => {
  const [last, first] = name.split(', ');
  return `${first?.charAt(0) || ''}${last?.charAt(0) || ''}`;
};

/**
 * A reducer like function to handle URL Search Params
 *
 * @param key name of the property
 *
 * @returns an array with value and setValue for the given property
 */
export const useParsedSearchParams = (key: 'asset' | 'floc' | 'certificate') => {
  const [searchParams, setSearchParams] = useSearchParams();

  return useMemo<[string, (value: string) => void]>(() => {
    let value: string, setValue: (value: string) => void;
    switch (key) {
      case 'asset':
        {
          const asset = searchParams.get('asset');
          value = asset ? asset : '';
          setValue = (asset: string) => {
            setSearchParams((sp) => {
              // Clear the floc selection
              sp.set('asset', asset);
              sp.delete('floc');
              return sp;
            });
          };
        }
        break;
      default:
        value = searchParams.get(key) || '';
        setValue = (value: string) => {
          setSearchParams((sp) => {
            sp.set(key, value);
            return sp;
          });
        };
    }
    return [value, setValue];
  }, [key, searchParams]);
};

export const useSelectedAsset = () => {
  const [assetName] = useParsedSearchParams('asset');
  const { data } = useQuery(GET_ALL_ASSETS);
  const asset = data?.allAssets?.find((asset) => asset?.assetName === assetName);

  return asset;
};

export const useEventId = () => {
  const { eventId: param } = useParams();
  return parseInt(param || '');
};

export const useJobId = () => {
  const { jobId: param } = useParams();
  return parseInt(param || '');
};

export const useCertificateId = () => {
  const { certificateId: param } = useParams();
  return parseInt(param || '');
};

export const useJointId = () => {
  const { jointId: param } = useParams();
  return parseInt(param || '');
};

export const useSelectedEvent = () => {
  const eventId = useEventId();
  const { data } = useQuery(GET_ALL_EVENTS);
  const { data: assetsData } = useQuery(GET_ALL_ASSETS);

  const event = data?.allEvents?.find((event) => event?.eventId == eventId);
  const asset = assetsData?.allAssets?.find((asset) => asset?.assetId === event?.assetId);

  return { event, asset };
};

export const useIsFeatureFlagEnabled = (featureFlagName: string) => {
  const { data: config } = useQuery(GET_CONFIG);
  return !!config?.config.featureFlags.find((ff) => ff.flag === featureFlagName)?.isEnabled;
};

export function addSpacesBetweenCapitals(text: string): string {
  return text.replace(/([a-z])([A-Z])/g, '$1 $2');
}
